import React, { useRef, useState } from 'react';
import { Paperclip, Upload, Loader2, UploadCloud, X, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { DocumentType } from '@/lib/patientDocumentsApi';

export interface DocumentUploadMeta {
  documentType: DocumentType;
  bodySite?: string;
  documentDate: string;
  photoConsentConfirmed: boolean;
}

interface DocumentUploadFormProps {
  onSubmit: (files: File[], meta: DocumentUploadMeta) => Promise<void> | void;
  submitting?: boolean;
  submitLabel?: string;
  title?: string;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// Presentational upload form — dropzone, type/date/body-site fields, consent
// checkbox. Shared between the "upload now" flow (PatientDocuments, when a
// real prescription/admission/patient link already exists) and the "stage
// for later" flow (PendingAttachments, used while a prescription is still
// being created and has no id yet) — same UI, different persistence.
export default function DocumentUploadForm({ onSubmit, submitting, submitLabel = 'Upload', title = 'Add a document' }: DocumentUploadFormProps) {
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [documentType, setDocumentType] = useState<DocumentType>('external_prescription');
  const [bodySite, setBodySite] = useState('');
  const [documentDate, setDocumentDate] = useState(todayIso());
  const [consentConfirmed, setConsentConfirmed] = useState(false);

  const resetForm = () => {
    setPendingFiles([]);
    setBodySite('');
    setDocumentDate(todayIso());
    setConsentConfirmed(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const addFiles = (incoming: FileList | File[]) => {
    setPendingFiles((prev) => [...prev, ...Array.from(incoming)]);
  };

  const removePendingFile = (idx: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!pendingFiles.length) return;
    if (documentType === 'clinical_photo' && !consentConfirmed) {
      toast({ title: 'Consent required', description: 'Confirm patient consent before adding a clinical photo.', variant: 'destructive' });
      return;
    }
    await onSubmit(pendingFiles, {
      documentType,
      bodySite: documentType === 'clinical_photo' ? bodySite : undefined,
      documentDate,
      photoConsentConfirmed: consentConfirmed,
    });
    resetForm();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current += 1;
    if (e.dataTransfer.types.includes('Files')) setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) { dragCounter.current = 0; setIsDragging(false); }
  };
  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden shadow-sm">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/70 bg-muted/30">
        <span className="icon-medical-complaints !bg-info/10 !text-info !p-1">
          <Paperclip className="w-3 h-3" />
        </span>
        <span className="text-xs font-semibold text-foreground">{title}</span>
      </div>

      <div className="p-3 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px] text-muted-foreground">Document Type</Label>
            <Select value={documentType} onValueChange={(v) => setDocumentType(v as DocumentType)}>
              <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="external_prescription">External Prescription</SelectItem>
                <SelectItem value="clinical_photo">Clinical Photo</SelectItem>
                <SelectItem value="identity_document">Insurance / ID Proof</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-muted-foreground">Document Date</Label>
            <Input type="date" className="h-8 text-xs mt-1" value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} />
          </div>
        </div>

        {documentType === 'clinical_photo' && (
          <div className="animate-fade-in">
            <Label className="text-[11px] text-muted-foreground">Body Site</Label>
            <Input
              className="h-8 text-xs mt-1"
              placeholder="e.g. Left forearm wound"
              value={bodySite}
              onChange={(e) => setBodySite(e.target.value)}
            />
          </div>
        )}

        {/* Dropzone */}
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={cn(
            'relative flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-3 py-4 cursor-pointer transition-all duration-150',
            isDragging
              ? 'border-info bg-info/10 scale-[1.01]'
              : 'border-border/70 bg-muted/20 hover:border-info/50 hover:bg-info/5'
          )}
        >
          <UploadCloud className={cn('w-5 h-5 transition-colors', isDragging ? 'text-info' : 'text-muted-foreground')} />
          <p className="text-xs text-foreground font-medium">
            {isDragging ? 'Drop to add' : 'Drag files here, or click to browse'}
          </p>
          <p className="text-[10px] text-muted-foreground">PDF, JPG, PNG, HEIC — up to 10MB each</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,application/pdf,image/*"
            className="hidden"
            onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }}
          />
        </div>

        {/* Pending file chips */}
        {pendingFiles.length > 0 && (
          <div className="flex flex-wrap gap-1.5 animate-fade-in">
            {pendingFiles.map((f, idx) => (
              <span
                key={`${f.name}-${idx}`}
                className="inline-flex items-center gap-1 rounded-full bg-info/10 border border-info/20 pl-2 pr-1 py-0.5 text-[10px] text-info max-w-[10rem]"
              >
                <span className="truncate">{f.name}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removePendingFile(idx); }}
                  className="flex-shrink-0 rounded-full hover:bg-info/20 p-0.5"
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </span>
            ))}
          </div>
        )}

        {documentType === 'clinical_photo' && (
          <div className="flex items-start gap-2 rounded-md bg-warning/5 border border-warning/20 px-2.5 py-2 animate-fade-in">
            <Checkbox
              id="photo-consent"
              checked={consentConfirmed}
              onCheckedChange={(v) => setConsentConfirmed(!!v)}
              className="mt-0.5"
            />
            <Label htmlFor="photo-consent" className="text-[11px] font-normal text-foreground/90 cursor-pointer leading-snug flex items-start gap-1">
              <ShieldCheck className="w-3 h-3 text-warning flex-shrink-0 mt-0.5" />
              Patient has consented to this clinical photograph being taken and stored.
            </Label>
          </div>
        )}

        <Button
          size="sm"
          className="h-8 text-xs w-full sm:w-auto"
          disabled={!pendingFiles.length || submitting || (documentType === 'clinical_photo' && !consentConfirmed)}
          onClick={handleSubmit}
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
          {submitLabel} {pendingFiles.length > 0 ? `(${pendingFiles.length})` : ''}
        </Button>
      </div>
    </div>
  );
}
